import {
  collection,
  doc,
  serverTimestamp,
  type FirestoreDataConverter,
  type PartialWithFieldValue,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type {
  FirestoreRoomStateFile,
  FirestoreRoomStateFileWrite,
} from './schema'
import { mimeTypeMapping, schema, writeSchema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import {
  deleteDocWithError,
  getDocWithError,
  setDocWithError,
} from '../../firestore-mobx/fetch'
import { RoomStateFile } from '../../models/RoomStateFile'
import { modelListStream } from '../../firestore-mobx/stream'
import { ref as storageRef } from 'firebase/storage'
import { safeDeleteStorageObject } from '../../util/safeDeleteStorageObject'

export { supportedExtensions } from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateFile> = {
  toFirestore: (data: PartialWithFieldValue<FirestoreRoomStateFile>) => {
    writeSchema.partial().parse(data)

    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: FirebaseRepository['firestore'],
  params: {
    roomStateId: string
  }
) => {
  const roomStateRef = doc(firestore, 'room_state', params.roomStateId)
  return collection(roomStateRef, 'file').withConverter(converter)
}

export const uploadRoomStateFile = async (
  repository: FirebaseRepository,
  {
    roomStateId,
    file,
  }: {
    roomStateId: string
    file: File
  }
) => {
  const extension = file.name.split('.').pop() || 'bin'
  const mimeType = mimeTypeMapping[extension]
  if (!mimeType) {
    throw new Error(`Unsupported file extension: ${extension}`)
  }

  const fileId = crypto.randomUUID()

  const fields: FirestoreRoomStateFileWrite = {
    fileLink: '',
    fileName: file.name,
    fileStorageURL: '',
    mimeType,
    roomId: roomStateId,
    userId: repository.uid,
    updatedAt: serverTimestamp(),
  }

  const storageRef = ref(
    repository.storage,
    `room_state/${roomStateId}/file/${fileId}.${extension}`
  )

  await uploadBytes(storageRef, file, {
    contentType: mimeType,
  })

  // get the download url and strip the token param
  const downloadUrl = await getDownloadURL(storageRef)
  const downloadURI = new URL(downloadUrl)
  downloadURI.searchParams.delete('token')
  const urlWithoutToken = downloadURI.toString()

  const fileRef = doc(getColRef(repository.firestore, { roomStateId }), fileId)

  fields.fileLink = urlWithoutToken
  fields.fileStorageURL = `gs://${storageRef.bucket}/${storageRef.fullPath}`

  await setDocWithError(fileRef, fields, {
    errorName: 'UploadSlideDeckMaterialFileError',
  })

  return urlWithoutToken
}

export const getRoomStateFiles = (
  repository: FirebaseRepository,
  params: {
    roomStateId: string
  }
) => {
  const ref = getColRef(repository.firestore, params)

  return modelListStream(repository, ref, RoomStateFile)
}

export const deleteRoomStateFile = async (
  repository: FirebaseRepository,
  params: {
    roomStateId: string
    fileId: string
  }
) => {
  const ref = doc(
    getColRef(repository.firestore, { roomStateId: params.roomStateId }),
    params.fileId
  )

  const storage = repository.storage

  const file = await getDocWithError(ref)

  const data = file.data() as FirestoreRoomStateFile

  if (data.fileLink) {
    const ref = storageRef(storage, data.fileLink)

    await safeDeleteStorageObject(ref)
  }

  await deleteDocWithError(ref, 'DeleteRoomStateFileError')
}
